/**
 * Which scheduled runs no longer belong to a forecast because they fired after its question
 * resolved — the client half of the server's rule (`server/lib/fanoutPointer.js`).
 *
 * The server stops appending such runs and prunes any that slipped in ahead of their
 * resolution notification. But the client's pointer merge is a union
 * (`reportPointerMerge.ts`), so an open tab would keep a pruned run forever. Applying the
 * SAME pure rule after every merge needs no ordering: a resolution is terminal, so a stale
 * snapshot that re-adds the run is pruned again the moment it lands.
 *
 * Only a run with a KNOWN fire time later than a KNOWN resolution is dropped. A pointer with
 * no parseable fire time predates the rule and stays; a voided question keeps its log.
 */

import { namedOutcomeIndex } from "./multiway.js";

// Terminal verdict tokens — the non-void half of lib/tracking.js's TERMINAL_RESOLUTION_STATUSES
// (a void keeps its log, so it is handled apart). Keep in step with server/lib/fanoutPointer.js.
const CONCLUSIVE = new Set(["resolved_yes", "resolved_no", "resolved_tie", "resolved_multi"]);
const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
const COMPACT = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/;
const DAY_MS = 86_400_000;

/** A calendar date that really exists — `Date.parse` rolls 2026-02-30 over to March 2. */
function realDate(ymd: string): boolean {
  const ms = Date.parse(`${ymd}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === ymd;
}

/** A run's fire time (ms): a zoned ISO instant or the run key's compact UTC stamp, else NaN. */
export function fireMs(fireTs: unknown): number {
  if (typeof fireTs !== "string" || !fireTs) return NaN;
  const c = COMPACT.exec(fireTs);
  if (c) {
    const [, y, mo, d, h, mi, s] = c;
    if (!realDate(`${y}-${mo}-${d}`) || +h! > 23 || +mi! > 59 || +s! > 59) return NaN;
    return Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
  }
  const m = INSTANT.exec(fireTs);
  if (!m || !realDate(m[1]!)) return NaN;
  return Date.parse(fireTs);
}

/** The last moment a run may fire and still belong (ms); a date-only value covers its day. */
function cutoffMs(resolvedAt: unknown): number | null {
  if (typeof resolvedAt !== "string") return null;
  if (PLAIN_DATE.test(resolvedAt))
    return realDate(resolvedAt) ? Date.parse(`${resolvedAt}T00:00:00Z`) + DAY_MS - 1 : null;
  const m = INSTANT.exec(resolvedAt);
  if (!m || !realDate(m[1]!)) return null;
  const ms = Date.parse(resolvedAt);
  return Number.isFinite(ms) ? ms : null;
}

/** What the rule reads off a run: its contract status and its resolution. */
export interface SettleSource {
  contractStatus?: unknown;
  resolution?: unknown;
}

/**
 * The resolution cutoff (ms) of a settled, non-voided run, or null when nothing dates a run
 * as "after": the question is open, voided, or its resolution moment is unknown.
 */
export function settleCutoffMs(run: SettleSource | null | undefined): number | null {
  if (!isSettled(run)) return null;
  const resolution = run!.resolution as Record<string, unknown>;
  return cutoffMs(resolution.resolved_at);
}

/**
 * Is this run's question voided — in EITHER form: the bus consumer's `contractStatus`, or
 * the on-demand resolver's `resolution.status`, which it can stamp before the status flips.
 * A void is final; it keeps its log but takes no new runs.
 */
export function isVoided(run: SettleSource | null | undefined): boolean {
  if (!run) return false;
  if (run.contractStatus === "voided") return true;
  const resolution =
    run.resolution && typeof run.resolution === "object"
      ? (run.resolution as Record<string, unknown>)
      : null;
  return resolution?.status === "voided";
}

/**
 * Has this run's question concluded (and not been voided)? Independent of WHEN: a final
 * verdict whose `resolved_at` is missing or malformed is still final — it just dates no run
 * as "after". Overlay precedence reads this; only pruning needs the cutoff.
 */
export function isSettled(run: SettleSource | null | undefined): boolean {
  if (!run || isVoided(run)) return false;
  const resolution =
    run.resolution && typeof run.resolution === "object"
      ? (run.resolution as Record<string, unknown>)
      : {};
  // Settled means CONCLUDED, exactly lib/tracking.js's isConcluded minus the void: a terminal
  // verdict, or a valid (non-negative integer) slot in the status-less bus form — under an
  // explicit status, say a later `unresolved`, a stale slot defers to it. A bare
  // contractStatus of "resolved" is NOT enough: with no verdict the app still offers a manual
  // reforecast, and that run must not be undone.
  return (
    CONCLUSIVE.has(resolution.status as string) ||
    (!resolution.status && namedOutcomeIndex(resolution) !== null)
  );
}

/** Did a run fired at `fireTs` fire after this run's question resolved? */
export function firedAfterResolution(
  run: SettleSource | null | undefined,
  fireTs: unknown,
): boolean {
  const cutoff = settleCutoffMs(run);
  return cutoff != null && fireMs(fireTs) > cutoff;
}

/**
 * `pointers` without the forecast runs that fired after `run` resolved — the same array
 * reference when nothing is dropped, so an unchanged run does not churn the store.
 */
export function withoutPostResolutionRuns<T>(pointers: T, run: SettleSource | null | undefined): T {
  if (!Array.isArray(pointers) || pointers.length === 0) return pointers;
  const cutoff = settleCutoffMs(run);
  if (cutoff == null) return pointers;
  const kept = pointers.filter((p) => {
    const e = p && typeof p === "object" ? (p as Record<string, unknown>) : null;
    return !(e && e.kind === "forecast" && fireMs(e.fire_ts) > cutoff);
  });
  return (kept.length === pointers.length ? pointers : kept) as T;
}

/** `serverContext` without its `resolution`, or null. */
function withoutResolution(ctx: unknown): unknown {
  if (!ctx || typeof ctx !== "object") return ctx ?? null;
  const { resolution: _stale, ...rest } = ctx as Record<string, unknown>;
  return rest;
}

/** A history snapshot as the reforecast paths record it (commands/reforecast.ts, reforecastPickup). */
interface HistorySnapshot {
  generatedAt?: string | null;
  snapshotAt?: string | null;
  contract?: unknown;
  events?: unknown;
  report?: unknown;
  serverContext?: unknown;
  serverExplanation?: unknown;
  serverPrediction?: unknown;
}

/** The run fields a rollback restores. */
export interface VintageRollback {
  contract: unknown;
  events: unknown[];
  report: unknown;
  serverContext: unknown;
  serverExplanation: unknown;
  serverPrediction: number | null;
  lastReforecastedAt: string | null;
  history: unknown[];
  superseded?: unknown[];
  vintageRestored?: boolean;
}

/**
 * Put back the forecast a post-resolution run replaced, when that run is the one on display.
 *
 * Reads one history invariant: an entry's `snapshotAt` is the moment a new vintage replaced
 * the state the entry records. So the FIRST entry stamped after the resolution records the
 * last vintage before the question settled. Restores it and trims the history back to it.
 * Null when the vintage on display belongs to the forecast, or no entry records the past.
 */
export function rollbackPostResolutionVintage(
  run:
    | (SettleSource & { lastReforecastedAt?: unknown; history?: unknown; superseded?: unknown })
    | null
    | undefined,
): VintageRollback | null {
  if (!run) return null;
  const history = Array.isArray(run.history) ? (run.history as HistorySnapshot[]) : [];
  // The moment of the vintage on display. `lastReforecastedAt` is not hydrated from rows, so
  // after a reload it is null and the last entry's `snapshotAt` — the moment the current
  // vintage replaced the one before it — stands in, the same convention the pickup uses.
  const onDisplayAt = run.lastReforecastedAt || history[history.length - 1]?.snapshotAt;
  if (!firedAfterResolution(run, onDisplayAt)) return null;
  const lateIn = (list: HistorySnapshot[]) =>
    list.findIndex((h) => h && firedAfterResolution(run, h.snapshotAt));
  // Two records can hold snapshots: the history, and the in-memory `superseded` list a
  // promotion that could date no entry used instead. A late promotion may land in either
  // (a legacy trail's first one in `superseded`, the next in history once it is dateable),
  // so the vintage to restore is the EARLIEST late snapshot across both.
  const undated = Array.isArray(run.superseded) ? (run.superseded as HistorySnapshot[]) : [];
  const inHistory = lateIn(history);
  const inUndated = lateIn(undated);
  if (inHistory < 0 && inUndated < 0) return null;
  const fromUndated =
    inHistory < 0 ||
    (inUndated >= 0 &&
      fireMs(undated[inUndated]!.snapshotAt) < fireMs(history[inHistory]!.snapshotAt));
  const prior = fromUndated ? undated[inUndated]! : history[inHistory]!;
  // The recorded headline; snapshots written before it was recorded fall back to the
  // contract's probability, the closest value they carry.
  const recorded = prior.serverPrediction;
  const probability =
    typeof recorded === "number" && Number.isFinite(recorded)
      ? recorded
      : (prior.contract as { probability?: unknown } | null | undefined)?.probability;
  return {
    contract: prior.contract ?? null,
    events: Array.isArray(prior.events) ? prior.events : [],
    report: prior.report ?? null,
    // The context predates the resolution, so it may hold a stale one (say `unresolved`):
    // restore everything else, never that — the run's own resolution stands.
    serverContext: withoutResolution(prior.serverContext),
    serverExplanation: prior.serverExplanation ?? null,
    serverPrediction:
      typeof probability === "number" && Number.isFinite(probability) ? probability : null,
    lastReforecastedAt: typeof prior.generatedAt === "string" ? prior.generatedAt : null,
    // Every late snapshot goes, from both records: each belongs to the run being undone.
    history: inHistory >= 0 ? history.slice(0, inHistory) : history,
    // Saves must carry the restored values as sent, empties included (see ContentSaveBody).
    vintageRestored: true,
    superseded: inUndated >= 0 ? undated.slice(0, inUndated) : undated,
  };
}
